import conductionLogo from '@/assets/images/conduction-logo.png';
import { SessionContext } from '@/context/SessionContext';
import { ActionIcon, Avatar, Box, Button, Flex, Group, Image, Menu, Text, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
	IconChevronDown,
	IconHeart,
	IconLogout,
	IconMessage,
	IconShoppingCart
} from '@tabler/icons-react';
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LoginModal from '../modals/login-modal';
import SongRequestModal from '../modals/song-request-modal';
import UserInterestsModal from '../modals/user-interests-modal';
import PasswordResetModal from '../modals/password-reset-modal';

const Header: React.FC = () => {
	const session = useContext(SessionContext);
	const navigate = useNavigate();

	const user = session?.user;
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [showUserInterestsModal, setShowUserInterestsModal] = useState(false);
	const [showSongRequestModal, setShowSongRequestModal] = useState(false);
	// Show password reset modal if the route matches `/password-reset/:userId/:token`
	const [showPasswordResetModal, setShowPasswordResetModal] = useState<boolean>(window.location.pathname.startsWith('/password-reset'));

	return (
		<Box px="md" mt="xs" style={{ width: '100%' }}>
			<LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
			<UserInterestsModal showModal={showUserInterestsModal} setShowModal={setShowUserInterestsModal} />
			<SongRequestModal showModal={showSongRequestModal} setShowModal={setShowSongRequestModal} />
			<PasswordResetModal showModal={showPasswordResetModal} setShowModal={setShowPasswordResetModal} />

			<Flex
				justify={'space-between'}
				align={'center'}
				w={"100%"}
				h={36}
				style={{
					width: '100%'
				}}
			>
				<Group gap={isMobile ? "0" : "sm"}>
					<Link
						to="https://www.conduction.live"
						target="_blank"
						rel="noopener noreferrer"
					>
						<Image
							src={conductionLogo}
							alt="Conduction"
							height={30}
							width={30}
							radius="md"
							style={{
								display: 'block',
								width: isMobile ? 20 : 30,
								height: isMobile ? 20 : 30,
								objectFit: 'contain',
								flexShrink: 0
							}}
						/>
					</Link>

					<Button
						variant="subtle"
						size={isMobile ? "8px" : "30px"}
						h={isMobile ? "20px" : "30px"}
						onClick={() => navigate('/')}
						bg={window.location.pathname === '/' ? 'blue' : ''}
						c={window.location.pathname === '/' ? 'white' : ''}
					>
						<Text size={isMobile ? "8px" : "14px"}>
							Home
						</Text>
					</Button>

					<Button
						variant="subtle"
						size={isMobile ? "10px" : "30px"}
						h={isMobile ? "20px" : "30px"}
						onClick={() => navigate('/search')}
						bg={window.location.pathname === '/search' ? 'blue' : ''}
						c={window.location.pathname === '/search' ? 'white' : ''}
					>
						<Text size={isMobile ? "8px" : "14px"}>
							Search
						</Text>
					</Button>
					<Button
						variant="subtle"
						size={isMobile ? "10px" : "30px"}
						h={isMobile ? "20px" : "30px"}
						onClick={() => navigate('/videos')}
						bg={window.location.pathname === '/videos' ? 'blue' : ''}
						c={window.location.pathname === '/videos' ? 'white' : ''}
					>
						<Text size={isMobile ? "8px" : "14px"}>
							Social
						</Text>
					</Button>

					{/* My Library */}
					{user != null && (
						<Button
							variant="subtle"
							size={isMobile ? "10px" : "30px"}
							h={isMobile ? "20px" : "30px"}
							onClick={() => navigate('/library')}
							bg={window.location.pathname === '/library' ? 'blue' : ''}
							c={window.location.pathname === '/library' ? 'white' : ''}
						>
							<Text size={isMobile ? "8px" : "14px"}>
								My Library
							</Text>
						</Button>
					)}

					
				</Group>
				<Group>
					{/* Instrumentalist Interface */}
					{user != null && user.roles.includes("Instrumentalist") && (
						<Button
									w={"auto"}
									h={isMobile ? 20 : 30}
									onClick={() => navigate(`/instrumentalist`)}
								>
									<Text fz={isMobile ? 8 : 14}>
										Instrumentalist Interface
									</Text>
								</Button>
					)}
					{user != null && (
						<Button
									w={"auto"}
									h={isMobile ? 20 : 30}
									onClick={() => navigate(`/video-editor`)}
								>
									<Text fz={isMobile ? 8 : 14}>
										Video Editor Interface
									</Text>
								</Button>
					)}
					{user == null ? (
						<>
							<Button
								variant="outline"
								size="xs"
								onClick={() => setShowLoginModal(true)}
							>
							
								<Text size="sm">
									Login
								</Text>
							</Button>
						</>
					) : (
						<>
							{user.artists.length === 1 && (
								<Button
									w={isMobile ? 40 : 100}
									h={isMobile ? 20 : 30}
									p={0}
									onClick={() => navigate(`/artists/${user.artists[0].artistId}/dashboard`)}
								>
									<Text fz={isMobile ? 8 : 14}>
										My Artist
									</Text>
								</Button>
							)}
							{user.artists.length > 1 && (
								<Menu
									transitionProps={{ transition: 'pop-top-right' }}
									trigger="hover"
									openDelay={100}
								>
									<Menu.Target>
										<Button
											variant="subtle"
											w={isMobile ? 70 : 120}
											h={isMobile ? 20 : 30}
											p={0}
											bg={theme.colors.blue[8]}
											c={theme.colors.blue[0]}
										>
											<Group gap="xs">
												<Text
													fz={isMobile ? 8 : 14}
												>
													My Artists
												</Text>
												<IconChevronDown size={16} />
											</Group>
										</Button>
									</Menu.Target>
									<Menu.Dropdown>
										{user.artists.map((artist: any) => (
											<Menu.Item
												key={artist.artistId}
												onClick={() => navigate(`/artists/${artist.artistId}/dashboard`)}
											>
												<Text size={isMobile ? "10px" : "14px"}>
													{artist.artistName}
												</Text>
											</Menu.Item>
										))}
									</Menu.Dropdown>
								</Menu>
							)}
							<Menu
								transitionProps={{ transition: 'pop-top-right' }}
								trigger="click-hover"
								openDelay={100}
							>
								<Menu.Target>
									<Button
										variant="subtle"
										w={isMobile ? "85px" : "175px"}
										p={0}
									>
										<Group
											gap="xs"
										>
											<Avatar
												alt={user.username}
												size={isMobile ? 16 : 24} />
											<Text
												fz={isMobile ? 8 : 14}
											>
												{user.username}
											</Text>
											<IconChevronDown size={16} />
										</Group>
									</Button>
								</Menu.Target>
								<Menu.Dropdown>
									<Menu.Item
										leftSection={<IconHeart size={16} color={theme.colors.yellow[6]} />}
										onClick={() => setShowUserInterestsModal(true)}
									>
										<Text size={isMobile ? "10px" : "14px"}>
											My Interests
										</Text>
									</Menu.Item>
									<Menu.Item
										leftSection={<IconMessage size={16} color={theme.colors.blue[6]} />}
										onClick={() => setShowSongRequestModal(true)}
									>
										<Text size={isMobile ? "10px" : "14px"}>
											Request A Song
										</Text>
									</Menu.Item>
									<Menu.Item
										leftSection={<IconLogout size={16} />}
										onClick={() => {
											session?.logout();
										}}
									>
										<Text size={isMobile ? "10px" : "14px"}>
											Logout ({user.username})
										</Text>
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
						</>
					)}
					<ActionIcon
						variant="subtle"
						aria-label="Cart"
						size={isMobile ? 20 : 36}
						onClick={() => navigate('/cart')}
					>
						<IconShoppingCart />
						<Text fz={isMobile ? 12 : 18}>{session?.songsInCart?.length}</Text>
					</ActionIcon>
				</Group>
			</Flex>
		</Box>
	);
}

export default Header;